import React, { useState } from 'react';
import { Play, Pause, RotateCcw, FastForward, ChevronUp, ChevronDown, Film, SkipBack, SkipForward } from 'lucide-react';

interface PlaybackControlsProps {
    isPlaying: boolean;
    currentStep: number;
    totalSteps: number;
    speed: number;
    onPlay: () => void;
    onPause: () => void;
    onRestart: () => void;
    onSpeedChange: (speed: number) => void;
    onStepBack?: () => void;
    onStepForward?: () => void;
    onSeek?: (index: number) => void;
    currentTimestamp?: Date;
}

export const PlaybackControls: React.FC<PlaybackControlsProps> = ({
    isPlaying,
    currentStep,
    totalSteps,
    speed,
    onPlay,
    onPause,
    onRestart,
    onSpeedChange,
    onStepBack,
    onStepForward,
    onSeek,
    currentTimestamp
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const speeds = [0.5, 1, 2, 5];

    const seekFromClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!onSeek || totalSteps <= 0) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        onSeek(Math.round(ratio * (totalSteps - 1)));
    };

    // Collapsed mini view
    if (isCollapsed) {
        return (
            <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-xl flex items-center gap-2">
                <Film className="w-4 h-4 text-primary" />
                <span className="text-xs text-foreground">Step {currentStep}/{totalSteps}</span>
                <button
                    type="button"
                    onClick={isPlaying ? onPause : onPlay}
                    className={`p-1.5 rounded border border-border ${
                        isPlaying
                            ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
                            : 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
                    }`}
                    aria-label={isPlaying ? 'Pause playback' : 'Play fund flow'}
                >
                    {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                </button>
                <div
                    className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden cursor-pointer"
                    onClick={seekFromClick}
                    title="Jump to step"
                    role="slider"
                    aria-valuenow={currentStep}
                    aria-valuemin={0}
                    aria-valuemax={Math.max(0, totalSteps - 1)}
                >
                    <div
                        className="h-full bg-gradient-to-r from-primary to-brand-gold-hover pointer-events-none"
                        style={{ width: `${totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0}%` }}
                    />
                </div>
                <button
                    type="button"
                    onClick={() => setIsCollapsed(false)}
                    className="p-1 hover:bg-muted rounded text-muted-foreground"
                    title="Expand controls"
                >
                    <ChevronUp className="w-4 h-4" />
                </button>
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-xl" data-tour="fund-flow-playback-controls">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs text-foreground">
                    <Film className="w-4 h-4 text-primary" />
                    <span className="font-medium">Fund Flow Playback</span>
                    <span className="text-muted-foreground font-normal" title="Times are blockchain confirmation timestamps (block time), shown in your local timezone.">
                        · on-chain times
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => setIsCollapsed(true)}
                    className="p-1 hover:bg-muted rounded text-muted-foreground"
                    title="Collapse controls"
                >
                    <ChevronDown className="w-4 h-4" />
                </button>
            </div>
            <div className="flex items-center gap-3">
                {onStepBack && (
                    <button
                        type="button"
                        onClick={onStepBack}
                        className="p-2 rounded-lg border border-border bg-muted text-foreground hover:bg-muted/80"
                        title="Previous transfer"
                        aria-label="Previous transfer"
                    >
                        <SkipBack className="w-4 h-4" />
                    </button>
                )}
                <button
                    type="button"
                    onClick={isPlaying ? onPause : onPlay}
                    className={`p-2 rounded-lg border border-border transition-all ${
                        isPlaying
                            ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300 hover:bg-amber-500/25'
                            : 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-500/25'
                    }`}
                    title={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                {onStepForward && (
                    <button
                        type="button"
                        onClick={onStepForward}
                        className="p-2 rounded-lg border border-border bg-muted text-foreground hover:bg-muted/80"
                        title="Next transfer"
                        aria-label="Next transfer"
                    >
                        <SkipForward className="w-4 h-4" />
                    </button>
                )}

                <button
                    type="button"
                    onClick={onRestart}
                    className="p-2 rounded-lg border border-border bg-sky-500/15 text-sky-800 dark:text-sky-300 hover:bg-sky-500/25 transition-all"
                    title="Restart"
                >
                    <RotateCcw className="w-4 h-4" />
                </button>

                <div className="flex-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                        <span>Step {currentStep} / {totalSteps}</span>
                        {currentTimestamp && (
                            <span
                                className="font-mono text-xs"
                                title="On-chain block time (local timezone)"
                            >
                                {currentTimestamp.toLocaleDateString()} {currentTimestamp.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                    <div
                        className="w-full h-1.5 bg-muted rounded-full overflow-hidden cursor-pointer"
                        onClick={seekFromClick}
                        title="Jump to step"
                        role="slider"
                        aria-valuenow={currentStep}
                        aria-valuemin={0}
                        aria-valuemax={Math.max(0, totalSteps - 1)}
                    >
                        <div
                            className="h-full bg-gradient-to-r from-primary to-brand-gold-hover transition-all duration-300 pointer-events-none"
                            style={{ width: `${totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0}%` }}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    <FastForward className="w-3 h-3 text-muted-foreground" />
                    <div className="flex gap-0.5">
                        {speeds.map((s) => (
                            <button
                                type="button"
                                key={s}
                                onClick={() => onSpeedChange(s)}
                                className={`px-2 py-1 rounded text-xs font-medium transition-all border border-border ${
                                    speed === s
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-foreground hover:bg-muted/80'
                                }`}
                            >
                                {s}x
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
